import React, { useEffect, useState } from 'react';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import CropImageField from '@components/common/form/CropImageField';
import DropdownField from '@components/common/form/DropdownField';
import FieldSet from '@components/common/form/FieldSet';
import RichTextField from '@components/common/form/RichTextField';
import TimePickerField from '@components/common/form/TimePickerField';
import { AppConstants, GoodsKinds, STATUS_ACTIVE, STATUS_LOCK, TIME_FORMAT_DISPLAY, ValueTypes } from '@constants';
import apiConfig from '@constants/apiConfig';
import { foodStatus, goodsKinds, goodsTypes, valueTypes } from '@constants/masterData';
import useFetch from '@hooks/useFetch';
import useTranslate from '@hooks/useTranslate';
import { NumericField, TextField } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import { showErrorMessage } from '@services/notifyService';
import { getBase64, handleRemoveImage } from '@utils';
import { getDisabledHour, getDisabledMinute } from '@utils/dayHelper';
import { Button, Col, Form, Row } from 'antd';

import BeilageForm from './BeilageForm';
import DayOfWeekToggle from './DayOfWeekToggle';

const GroupFoodForm = ({ formId, isEditing, isSubFood, dataDetail, onSubmit, openModalSelectBeilageTemplate, handleAddMoreBeilage, searchFood, restaurantDetail, foods, searchFoodLoading, formRef: externalFormRef, form, imageUrl, setImageUrl }) => {
    const translate = useTranslate();
    const [imageUploading, setImageUploading] = useState(false);
    const [goodsKind, setGoodsKind] = useState(isSubFood ? GoodsKinds.COMMON : dataDetail?.kind);
    const [errDefaultPrinter, setErrDefaultPrinter] = useState(false);
    const [isAllwaysAsk, setIsAllwaysAsk] = useState(
        dataDetail?.settings && dataDetail?.settings.printers && dataDetail?.settings.printers.type,
    );
    const watchedGoodsKind = Form.useWatch('kind', form) || goodsKind;

    const goodsKindsValues = translate.formatKeys(goodsKinds, ['label']);
    const foodStatusValues = translate.formatKeys(foodStatus, ['label']);
    const goodsTypesValues = translate.formatKeys(goodsTypes, ['label']);
    const valueTypesValues = translate.formatKeys(valueTypes, ['label']);
    const getPriceType = (setting) => {
        if (!setting || Object.keys(setting).length === 0) {
            return {};
        }

        return Object.keys(setting).reduce((result, key) => ({
            ...result,
            [key]: Array.isArray(setting[key])
                ? setting[key].map((el) => el.price_type)
                : [],
        }), {});
    };

    const getTimeFrames = () => (restaurantDetail?.happy_hours_settings ? restaurantDetail.happy_hours_settings?.map((happuHour) => ({ value: happuHour.timeframe, label: happuHour.timeframe })) : []);

    const [priceTypes, setPriceTypes] = useState(getPriceType(dataDetail?.happyHoursSetting ? dataDetail?.happyHoursSetting : {}));

    const timeFrames = getTimeFrames();

    useEffect(() => {
        if (externalFormRef) externalFormRef.current = form;
    }, [form, externalFormRef]);

    useEffect(() => {
        if (!isEditing) {
            if (watchedGoodsKind === GoodsKinds.CHOOSE) {
                form.setFieldsValue({ status: STATUS_LOCK });
            } else {
                form.setFieldsValue({ status: STATUS_ACTIVE });
            }
        }
    }, [watchedGoodsKind, isEditing]);

    useEffect(() => {
        if (dataDetail) {
            form.setFieldsValue({
                ...dataDetail,
                productInfo: dataDetail?.productInfo || null,
                timeFrom: dataDetail.timeFrom,
                timeEnd: dataDetail.timeEnd,
                ...(isEditing ? {} : {
                    status: STATUS_ACTIVE,
                    kind: GoodsKinds.COMMON,
                }),
            });
        }
    }, [form, isEditing]);

    useEffect(() => {
        if (dataDetail && isEditing) {
            setImageUrl(dataDetail?.imagePath);
        }
    }, [dataDetail]);

    const { execute: executeUpFile } = useFetch({
        ...apiConfig.file.upload,
        isRequiredTenantId: true,
    });

    const uploadFile = (file) => {
        executeUpFile({
            data: {
                type: 'LOGO',
                file: file,
            },
            onCompleted: (response) => {
                if (response.result === true) {
                    setImageUrl(response.data.filePath);
                }
            },
            onError: (error) => { },
        });
    };

    const handleSubmit = (formValues) => {
        const printerCodes = form.getFieldValue(['settings', 'printers', 'printerCode']);
        if (!isAllwaysAsk && printerCodes?.length > 0) {
            const hasDefault = printerCodes.some((answer) => answer.isDefault);
            if (hasDefault) {
                onSubmit(formValues);
            } else {
                setErrDefaultPrinter(true);
            }
        } else {
            onSubmit(formValues);
        }
    };

    const checkAddHappyHour = (addCallback, groupFieldName) => {
        const happhours = form.getFieldValue(groupFieldName);
        if (happhours?.length > 0 && !happhours[happhours.length - 1]) {
            showErrorMessage('foodList:message.requireTimeFrame', translate);
        } else {
            addCallback();
        }
    };

    const updatePriceType = (group, updateFunc) => {
        setPriceTypes((prev) => ({ ...prev, [group]: updateFunc([...(prev[group] || [])]) }));
    };

    const renderHappyHoursSetting = (groupFieldName) => {
        const groupPriceTypeName = groupFieldName.slice(-1)[0];
        return (
            <Row gutter={16}>
                <Col span={24}>
                    <Form.List name={groupFieldName}>
                        {(fields, { add, remove }) => (
                            <div className="small-form-items">
                                {fields.map((field, index) => (
                                    <Row gutter={16} key={index}>
                                        <Col span={6}>
                                            <DropdownField
                                                fieldName={[field.name, 'time_frame']}
                                                placeholder={translate.formatMessage(commonMessage.timeFrame)}
                                                required
                                                requiredMsg={translate.formatMessage(commonMessage.requireTimeFrame)}
                                                options={timeFrames.filter((timeFrame) => {
                                                    const timeFrameSelected = form.getFieldValue(groupFieldName);
                                                    return !timeFrameSelected?.some((tf) => tf?.time_frame === timeFrame.value);
                                                })}
                                            />
                                        </Col>
                                        <Col span={6}>
                                            <DropdownField
                                                fieldName={[field.name, 'price_type']}
                                                placeholder={translate.formatMessage(commonMessage.valueType)}
                                                required
                                                requiredMsg={translate.formatMessage(commonMessage.requireValueType)}
                                                options={valueTypesValues}
                                                onChange={(type) => updatePriceType(groupPriceTypeName, (types) => {
                                                    types[index] = type;
                                                    return types;
                                                })}
                                            />
                                        </Col>
                                        <Col span={6}>
                                            <NumericField
                                                formatter={(v) => v}
                                                parser={(v) => v}
                                                min={0}
                                                name={[field.name, 'price']}
                                                width="100%"
                                                placeholder={translate.formatMessage(commonMessage.price)}
                                                required
                                                {...(priceTypes[groupPriceTypeName]?.[index] === ValueTypes.PERCENTAGE ? { max: 100 } : {})}
                                            />
                                        </Col>
                                        <Col>
                                            <MinusCircleOutlined
                                                onClick={() => {
                                                    remove(field.name);
                                                    updatePriceType(groupPriceTypeName, (types) => {
                                                        types.splice(index, 1);
                                                        return types;
                                                    });
                                                }}
                                            />
                                        </Col>
                                    </Row>
                                ))}
                                {fields.length > 19 ? null : (
                                    <Form.Item style={{ width: '184px' }}>
                                        <Button
                                            type="dashed"
                                            onClick={() =>
                                                checkAddHappyHour(() => {
                                                    add({ price_type: ValueTypes.FIXED });
                                                    updatePriceType(groupPriceTypeName, (types) => [...types, ValueTypes.FIXED]);
                                                }, groupFieldName)
                                            }
                                            block
                                        >
                                            <PlusOutlined /> {translate.formatMessage(commonMessage.addHappyHour)}
                                        </Button>
                                    </Form.Item>
                                )}
                            </div>
                        )}
                    </Form.List>
                </Col>
            </Row>
        );
    };

    const onSelectKind = (value) => setGoodsKind(value);

    const onSelectTimeSale = (fieldName, value) => {
        if (fieldName === 'timeFrom') {
            const timeEnd = form.getFieldValue('timeEnd');
            if (timeEnd && value && value.format(TIME_FORMAT_DISPLAY) > timeEnd.format(TIME_FORMAT_DISPLAY)) {
                form.setFieldsValue({ timeEnd: null });
            }
        } else if (fieldName === 'timeEnd') {
            const timeFrom = form.getFieldValue('timeFrom');
            if (timeFrom && value && value.format(TIME_FORMAT_DISPLAY) < timeFrom.format(TIME_FORMAT_DISPLAY)) {
                form.setFieldsValue({ timeEnd: timeFrom });
            }
        }
        form.setFieldsValue({ [fieldName]: value });
    };

    return (
        <Form form={form} id={formId} layout="vertical" onFinish={handleSubmit}>
            <Row gutter={16}>
                <Col span={12}>
                    <TextField name="name" label={translate.formatMessage(commonMessage.foodName)} required />
                </Col>
                <Col span={12}>
                    <TextField name="description" label={translate.formatMessage(commonMessage.description)} type="textarea" />
                </Col>
            </Row>
            <Row gutter={16}>
                <Col span={12}>
                    <CropImageField
                        name="imagePath"
                        loading={imageUploading}
                        label={translate.formatMessage(commonMessage.image)}
                        imageUrl={imageUrl && `${AppConstants.contentRootUrl}${imageUrl}`}
                        uploadFile={uploadFile}
                        handleRemoveImage={() => handleRemoveImage(setImageUrl, form)}
                        aspect={1}
                        style={{ width: 170 }}
                        requiredMsg={translate.formatMessage(commonMessage.requireImage)}
                    />
                </Col>
            </Row>
            <Row gutter={16}>
                <Col span={8}>
                    <DropdownField fieldName="status" label={translate.formatMessage(commonMessage.status)} required options={foodStatusValues} disabled={!isEditing && watchedGoodsKind === GoodsKinds.CHOOSE} />
                </Col>
                <Col span={8}>
                    <DropdownField fieldName="type" label={translate.formatMessage(commonMessage.type)} required options={goodsTypesValues} />
                </Col>
            </Row>
            <Row gutter={16}>
                {!isSubFood && (
                    <Col span={8}>
                        <DropdownField
                            fieldName="kind"
                            label={translate.formatMessage(commonMessage.kind)}
                            required
                            options={goodsKindsValues}
                            onSelect={onSelectKind}
                            disabled={isEditing}
                        />
                    </Col>
                )}
                {watchedGoodsKind === GoodsKinds.COMMON && (
                    <>
                        <Col span={8}>
                            <TextField required name="plu" label={translate.formatMessage(commonMessage.plu)} />
                        </Col>
                        <Col span={8}>
                            <TextField name="systemPosPlu" label={translate.formatMessage(commonMessage.systemPosPLU)} />
                        </Col>
                    </>
                )}
            </Row>
            <Row gutter={16}>
                <Col span={10}>
                    <div className="ant-row ant-form-item">
                        <div className="ant-col ant-form-item-label">
                            <label className="ant-form-item">{translate.formatMessage(commonMessage.timeSale)}</label>
                        </div>
                        <Row gutter={16}>
                            <Col span={12}>
                                <TimePickerField
                                    fieldName="timeFrom"
                                    placeholder={translate.formatMessage(commonMessage.timeStart)}
                                    width="100%"
                                    format={TIME_FORMAT_DISPLAY}
                                    onSelect={(value) => onSelectTimeSale('timeFrom', value)}
                                />
                            </Col>
                            <Col span={12}>
                                <TimePickerField
                                    fieldName="timeEnd"
                                    placeholder={translate.formatMessage(commonMessage.timeEnd)}
                                    width="100%"
                                    format={TIME_FORMAT_DISPLAY}
                                    onSelect={(value) => onSelectTimeSale('timeEnd', value)}
                                    disabledHours={() => getDisabledHour(form.getFieldValue('timeFrom'))}
                                    disabledMinutes={(hour) => getDisabledMinute(hour, form.getFieldValue('timeFrom'))}
                                />
                            </Col>
                        </Row>
                        <div style={{ marginTop: '8px' }}>
                            <DayOfWeekToggle fieldName={['settings', 'timeOffs']} label={translate.formatMessage(commonMessage.timeOffs)} />
                        </div>
                    </div>
                </Col>
            </Row>
            <Row gutter={16}>
                <Col span={24}>
                    <RichTextField label={translate.formatMessage(commonMessage.productInfo)} name="productInfo" />
                </Col>
            </Row>
            {watchedGoodsKind === GoodsKinds.COMMON && (
                <>
                    <FieldSet title={translate.formatMessage(commonMessage.qrCode)} className="bold-fieldset custom-fieldset">
                        <FieldSet title={translate.formatMessage(commonMessage.price)}>
                            <Row gutter={16}>
                                <Col span={6}>
                                    <NumericField
                                        formatter={(v) => v}
                                        parser={(v) => v}
                                        placeholder={translate.formatMessage(commonMessage.inPrice)}
                                        name={['price', 'qr_code', 'in_price']}
                                        label={translate.formatMessage(commonMessage.inPrice)}
                                        required
                                        width="100%"
                                    />
                                </Col>
                                <Col span={6}>
                                    <NumericField
                                        formatter={(v) => v}
                                        parser={(v) => v}
                                        placeholder={translate.formatMessage(commonMessage.inSaleOff)}
                                        name={['price', 'qr_code', 'in_sale_off_percent']}
                                        label={translate.formatMessage(commonMessage.inSaleOff)}
                                        width="100%"
                                        min={0}
                                        max={100}
                                    />
                                </Col>
                                <Col span={6}>
                                    <NumericField
                                        formatter={(v) => v}
                                        parser={(v) => v}
                                        placeholder={translate.formatMessage(commonMessage.outPrice)}
                                        name={['price', 'qr_code', 'out_price']}
                                        label={translate.formatMessage(commonMessage.outPrice)}
                                        required
                                        width="100%"
                                    />
                                </Col>
                                <Col span={6}>
                                    <NumericField
                                        formatter={(v) => v}
                                        parser={(v) => v}
                                        placeholder={translate.formatMessage(commonMessage.outSaleOff)}
                                        name={['price', 'qr_code', 'out_sale_off_percent']}
                                        label={translate.formatMessage(commonMessage.outSaleOff)}
                                        width="100%"
                                        min={0}
                                        max={100}
                                    />
                                </Col>
                            </Row>
                        </FieldSet>
                        <FieldSet title={translate.formatMessage(commonMessage.happyHourIn)}>{renderHappyHoursSetting(['happyHoursSetting', 'qrcode_in'])}</FieldSet>
                        <FieldSet title={translate.formatMessage(commonMessage.happyHourOut)}>{renderHappyHoursSetting(['happyHoursSetting', 'qrcode_out'])}</FieldSet>
                    </FieldSet>
                    <br />
                    <FieldSet title={translate.formatMessage(commonMessage.pickup)} className="bold-fieldset custom-fieldset">
                        <FieldSet title={translate.formatMessage(commonMessage.price)}>
                            <Row gutter={16}>
                                <Col span={6}>
                                    <NumericField formatter={(v) => v} parser={(v) => v} placeholder={translate.formatMessage(commonMessage.price)} name={['price', 'pickup', 'price']} label={translate.formatMessage(commonMessage.price)} width="100%" />
                                </Col>
                                <Col span={6}>
                                    <NumericField
                                        formatter={(v) => v}
                                        parser={(v) => v}
                                        placeholder={translate.formatMessage(commonMessage.saleOff)}
                                        name={['price', 'pickup', 'sale_off_percent']}
                                        label={translate.formatMessage(commonMessage.saleOff)}
                                        width="100%"
                                        min={0}
                                        max={100}
                                    />
                                </Col>
                            </Row>
                        </FieldSet>
                        <FieldSet title={translate.formatMessage(commonMessage.happyHour)}>{renderHappyHoursSetting(['happyHoursSetting', 'pickup'])}</FieldSet>
                    </FieldSet>
                    <br />
                    <FieldSet title={translate.formatMessage(commonMessage.deliver)} className="bold-fieldset custom-fieldset">
                        <FieldSet title={translate.formatMessage(commonMessage.price)}>
                            <Row gutter={16}>
                                <Col span={6}>
                                    <NumericField formatter={(v) => v} parser={(v) => v} placeholder={translate.formatMessage(commonMessage.price)} name={['price', 'deliver', 'price']} label={translate.formatMessage(commonMessage.price)} width="100%" />
                                </Col>
                                <Col span={6}>
                                    <NumericField
                                        formatter={(v) => v}
                                        parser={(v) => v}
                                        placeholder={translate.formatMessage(commonMessage.saleOff)}
                                        name={['price', 'deliver', 'sale_off_percent']}
                                        label={translate.formatMessage(commonMessage.saleOff)}
                                        width="100%"
                                        min={0}
                                        max={100}
                                    />
                                </Col>
                            </Row>
                        </FieldSet>
                        <FieldSet title={translate.formatMessage(commonMessage.happyHour)}>{renderHappyHoursSetting(['happyHoursSetting', 'delivery'])}</FieldSet>
                    </FieldSet>
                    <br />
                    <BeilageForm openModalSelectBeilageTemplate={openModalSelectBeilageTemplate} handleAddMoreBeilage={handleAddMoreBeilage} form={form} />
                    <FieldSet title={translate.formatMessage(commonMessage.printer)} className="bold-fieldset custom-fieldset">
                        <Row gutter={16}>
                            <Col span={6}>
                                <TextField name="printers" label={translate.formatMessage(commonMessage.printer)} />
                            </Col>
                        </Row>
                    </FieldSet>
                </>
            )}
        </Form>
    );
};

export default GroupFoodForm;